import './messageHandlers/register';
import { createReducer, createAction } from '@reduxjs/toolkit';
import ReactNativeBlobUtil from 'react-native-blob-util';
import moment, { Moment } from 'moment';
import i18n from 'i18n-js';
import { backendCall, eventCall, createApiCall } from '@reducers/utils';
import type { AppDispatch } from '@reducers/utils/types';
import type { RootState, AppThunk } from '@store/types';

import {
  machineStatus as machineStatuses,
  isWeb,
  ncFileStatuses,
  navigationRef,
  localTime,
  ALL_MACHINES,
  Alert,
  isSameDay,
  addQueryParams,
  MACHINE_REFERENCE_ACTION,
  SourceSystems,
  parseRouteParamAsNumber,
  injectConditionalObject,
} from '../../helpers';
import { generateNCFileURL } from '@reducers/blobStorage';
import { INDIRECT_MACHINE_LINK_SCOPE_IDS, KEYS_TAGS } from '@components/imageHandling/common';
import { loadSingleBatch } from '@reducers/batch';
import { trackEvent, TRACKING_EVENTS, updateUserProperties, getErrorType } from '@services/tracking';
import {
  getDemoTimelineEvents,
  getDemoNcFileContent,
  getDemoMachineStatsHour,
  getDemoMachineStatsHourPrev,
  isTourActive,
} from '../../components/tours/helpers/demoDataRegistry';

import type {
  MachinesState,
  ScheduleProductionOperationParams,
  WallboardEventTimelineParams,
  MachineEventTimelineParams,
  MachineOperationsSearchParams,
  MachineProductionBatchesParams,
  MachineAlarmEventParams,
  NCFileStatisticsParams,
  MachineStatisticsParams,
  MachineStatisticsForNCFileParams,
  ConfigureMachineReferencesParams,
  ConfigureOperationsParams,
  WallboardTimelineEventData,
  WallboardTimelineNotLoadedPayload,
  Machine,
  MachineEvent,
  MachineGroup,
  Operation,
  MachineReference,
  MachineAddress,
  MachineStatisticsData,
  NcFileStatisticsData,
  StatsParams,
  MachineEventParams,
  BatchTimelineParams,
  MachineHistoryParams,
  SendNcFileToMachineRequestDto,
  ConfigureProductionOperationRequestDto,
  StartMachineOnboardingSessionRequestDto,
  StartDemoMachineOnboardingSessionRequestDto,
  ValidateMachineEndpointRequestDto,
  ValidateMachineByAutomaticControlRequestDto,
  ValidateMachineByBrandRequestDto,
  ValidateMachineByConnectionMethodRequestDto,
  OnboardMachineRequestDto,
  OnboardDemoMachineRequestDto,
  SearchRequestParams,
  ConfigureMachineReferencesResponse,
  OperationsParams,
  SourceSystem,
  ConfigureMachineRequestDto,
  Machine2,
} from './types';
import type { Document } from '@api/domain/types';
import type { ConfigureMachineReferencesRequestDto } from '@api/generated/models/configureMachineReferencesRequestDto';
import type { ConfigureMachineGroupRequestDto } from '@api/generated/models/configureMachineGroupRequestDto';
import type { ConfigureOperationRequestDto } from '@api/generated/models/configureOperationRequestDto';
import type { SwitchMachineConnectionRequestDto } from '@api/generated/models/switchMachineConnectionRequestDto';
import { buildStatsGraphParams, buildStatsListParams } from '@reducers/statistics/helpers/buildStatisticsParams';
import type { MachineStatisticsRequestDto, NcFileStatisticsRequest2Dto } from '@api/domain/types';
import type { MachineStatisticsResponse } from '@api/domain/types';
import type { MachineStatistic, MachineStatisticPerPeriod, MachineStatisticsInput } from '@reducers/statistics/types';

// Re-export types for consumers
export type { MachinesState, Machine, MachineGroup, MachineEvent, Operation, MachineReference } from './types';

const initState: MachinesState = {
  machineAddresses: [],
  loadedMachineAddresses: false,
  loadedMachineAddressesError: false,
  machines: [],
  machinesLoaded: false,
  isError: false,
  timeOffset: 0,
  selectedItem: {},
  selectedNCFileItem: {},
  selectedTime: 'now',
  requestTimeEndMS: 0,
  refreshInfo: {},
  minimumTileSize: 2,
  machineStatistics: {
    data: [],
    maxValueActive: 0,
    maxValueProductive: 0,
  },
  machineStatisticsForNCFile: {
    data: [],
    maxValueActive: 0,
    maxValueProductive: 0,
  },
  ncFileStatistics: {
    data: [],
    page: 1,
    count: 100,
    isMore: true,
    maxValueActive: 0,
    maxValueProductive: 0,
  },
  wallboardTimelineStorage: {},
  wallboardTimelineStorageLoaded: {},
  wallboardSelectedLevel: 'live',
  wallboardTimelineEndDate: localTime(),
  machinesGroup: [],
  machinesReferences: [],
  machinesOperations: [],
  machinesOperationsLoaded: false,
  machinesGroupLoaded: false,
  machinesReferencesLoaded: false,
  updatedMachineEventsTimelineHash: null,
  inspectedMachine: null,
};

export const selectSelectedItem = (state: RootState) => state.machines.selectedItem;
export const selectMachines = (state: RootState) => state.machines.machines;
export const selectMachinesLoaded = (state: RootState) => state.machines.machinesLoaded;
export const selectMachinesIsError = (state: RootState) => state.machines.isError;
export const selectMachinesStatistics = (state: RootState) => state.machines.machineStatistics;
export const selectMachinesGroup = (state: RootState) => state.machines.machinesGroup;
export const selectUpdatedMachineEventsTimelineHash = (state: RootState) => state.machines.updatedMachineEventsTimelineHash;
export const selectMachinesOperations = (state: RootState) => state.machines.machinesOperations;
export const selectMachinesOperationsLoaded = (state: RootState) => state.machines.machinesOperationsLoaded;
export const selectInspectedMachine = (state: RootState) => state.machines.inspectedMachine;

export const saveMachineAddresses = createAction<MachineAddress[]>('machines/saveMachineAddresses');
export const saveMachineAddress = createAction<Machine>('machines/saveMachineAddress');
export const requestMachineAddresses = createAction('machines/requestMachineAddresses');
export const requestMachineAddressesError = createAction('machines/requestMachineAddressesError');
export const setSelectedMachine = createAction<Partial<Machine>>('machines/setSelectedMachine');
export const setSelectedNCFile = createAction<Record<string, unknown>>('machines/setSelectedNCFile');
export const setSelectedTime = createAction<string>('machines/setSelectedTime');
export const setMachines = createAction<Machine[]>('machines/setMachines');
export const clearMachineImageByDocumentId = createAction<{ documentId: number; machineId?: number }>('machines/clearMachineImageByDocumentId');
export const setMachineImage = createAction<{ machineId: number; machineImage: NonNullable<Machine['machineImage']> }>('machines/setMachineImage');
export const applyMachineImageByIndirectLinks = createAction<{
  links: NonNullable<Document['links']>;
  machineImage: NonNullable<Machine['machineImage']>;
}>('machines/applyMachineImageByIndirectLinks');
export const replaceMachine = createAction<Machine>('machines/replaceMachine');
export const setMachineStatistics = createAction<MachineStatisticsData>('machines/setMachineStatistics');
export const setMachineStatisticsForNCFile = createAction<MachineStatisticsData>('machines/setMachineStatisticsForNCFile');
export const setMachineBlobAccessInfo = createAction('machines/setMachineBlobAccessInfo');
export const refreshInfo = createAction<Record<string, unknown>>('machines/refreshInfo');
export const setMachinesError = createAction('machines/setMachinesError');
export const setNCFileStatistics = createAction<NcFileStatisticsData>('machines/setNCFileStatistics');
export const setMachinesGroup = createAction<MachineGroup[]>('machines/setMachinesGroup');
export const setMachinesReferences = createAction<MachineReference[]>('machines/setMachinesReferences');
export const setMachinesReferencesLoaded = createAction<boolean>('machines/setMachinesReferencesLoaded');
export const setMachinesOperationsLoaded = createAction<boolean>('machines/setMachinesOperationsLoaded');
export const setMachinesOperations = createAction<Operation[]>('machines/setMachinesOperations');
export const upsertMachinesOperation = createAction<Operation>('machines/upsertMachinesOperation');
export const setInspectedMachine = createAction<Machine | null>('machines/setInspectedMachine');
export const clearInspectedMachine = createAction('machines/clearInspectedMachine');

export const onUpdatedMachineEventsTimeline = createAction('machines/onUpdatedMachineEventsTimeline');

export const setRequestTimeEnd = createAction<number>('machines/setRequestTimeEnd');
export const setMinimumTileSize = createAction<number>('machines/setMinimumTileSize');
export const saveWallboardEventTimeline = createAction<WallboardTimelineEventData>('machines/saveWallboardEventTimeline');
export const setWallboardSelectedLevel = createAction<string>('machines/setWallboardSelectedLevel');
export const setWallboardTimelineEndDate = createAction<Moment>('machines/setWallboardTimelineEndDate');
export const setWallboardTimelineStorageNotLoaded = createAction<WallboardTimelineNotLoadedPayload>('machines/setWallboardTimelineStorageNotLoaded');

export const clearMachines = createAction('machines/clearMachines');
export const clear = createAction('clear');

// Demo tour actions
export const injectDemoMachine = createAction<Machine>('machines/injectDemoMachine');
export const prependDemoMachine = createAction<Machine>('machines/prependDemoMachine');
export const removeDemoMachine = createAction<number>('machines/removeDemoMachine');

const mapMachine = (m: Partial<Machine> | undefined | null): Partial<Machine> => {
  if (!m) {
    return {};
  }
  const machineStatus = m.machineStatus ? m.machineStatus : {};
  return {
    ...m,
    id: m.machineId,
    machineStatus,
  };
};

export const availableMachines = () => async (dispatch: AppDispatch) => {
  dispatch(requestMachineAddresses());

  const response = await dispatch(backendCall(createApiCall((api) => api.machines.availableMachines, { params: {} })));

  if (response?.machineAddresses) {
    dispatch(saveMachineAddresses(response.machineAddresses));
  } else {
    dispatch(requestMachineAddressesError());
  }
};

export const onRefreshedMachineAddressList =
  (data: Record<string, unknown>): AppThunk =>
  async (dispatch, getState) => {
    const {
      machines: { machineAddresses },
    } = getState();

    const ignoreList: string[] = [];
    const updateList: Record<string, unknown> = {};
    if ((data as Record<string, unknown>)?.machineAddressEntries) {
      ((data as Record<string, unknown>).machineAddressEntries as Array<Record<string, unknown>>).map((i) => {
        switch (i.machineAddressState) {
          case 'Remove': {
            ignoreList.push((i.machineAddress as Record<string, unknown>).endpoint as string);
            break;
          }
          case 'Store': {
            updateList[(i.machineAddress as Record<string, unknown>).endpoint as string] = i.machineAddress;
            break;
          }
        }
      });
    }

    const newList = machineAddresses
      .filter((i: MachineAddress) => !ignoreList.includes(i.endpoint as string))
      .map((i: MachineAddress) => {
        if (updateList[i.endpoint as string]) {
          return updateList[i.endpoint as string] as MachineAddress;
        }
        return i;
      });

    dispatch(saveMachineAddresses(newList));
  };

export const sendNcFileToMachine = (data: SendNcFileToMachineRequestDto) => async (dispatch: AppDispatch) => {
  try {
    const resp = await dispatch(
      eventCall({
        connectionName: 'appHub',
        eventName: 'OnSentNcFileToMachine',
        eventNameError: 'OnFailedToSendNcFileToMachine',
        data: createApiCall((api) => api.machines.sendNcFileToMachine, { params: data }),
        timeout: 120,
      })
    );
    trackEvent(TRACKING_EVENTS.NC_FILE, {
      action: 'sentToMachine',
      ncFileId: String(data.ncFileId || ''),
      machineId: String(data.machineId || ''),
      machineName: data.machineName || '',
      versionNumber: data.versionNumber || 0,
      isFavoriteVersion: !!data.isFavoriteVersion,
      result: 'success',
    });
    return resp;
  } catch (e) {
    trackEvent(TRACKING_EVENTS.NC_FILE, {
      action: 'sentToMachine',
      ncFileId: String(data.ncFileId || ''),
      machineId: String(data.machineId || ''),
      machineName: data.machineName || '',
      versionNumber: data.versionNumber || 0,
      isFavoriteVersion: !!data.isFavoriteVersion,
      result: 'failed',
      errorType: getErrorType(e),
    });
    console.log(e, 'e');
    return e;
  }
};

export const configureProductionOperation = (data: ConfigureProductionOperationRequestDto) => async (dispatch: AppDispatch) => {
  try {
    const resp = await dispatch(
      eventCall({
        connectionName: 'appHub',
        eventName: 'OnConfiguredProductionOperation',
        eventNameError: 'OnFailedToChangeProductionInformation',
        data: createApiCall((api) => api.machines.configureProductionOperation, { params: data }),
        timeout: 120,
      })
    );
    return resp;
  } catch (e) {
    console.log(e, 'e');
    return e;
  }
};

export const scheduleProductionOperationOnMachine =
  ({ productionOperationId, machineId }: ScheduleProductionOperationParams) =>
  async (dispatch: AppDispatch) => {
    const correlationId = crypto.randomUUID();
    await dispatch(
      backendCall(
        createApiCall((api) => api.productionInformation.scheduleProductionOperation, {
          params: { correlationId, productionOperationId, machineId },
        })
      )
    );
  };

export const startMachineOnboardingSession =
  (machine: Record<string, unknown>, isDemoMachine: boolean): AppThunk<Promise<string | null>> =>
  async (dispatch) => {
    try {
      if (isDemoMachine) {
        const data: StartDemoMachineOnboardingSessionRequestDto = {
          endpoint: machine.endpoint as string | undefined,
        };
        const resp = await dispatch(
          eventCall({
            connectionName: 'appHub',
            eventName: 'OnStartedMachineOnboardingSession',
            data: createApiCall((api) => api.machines.startDemoMachineOnboardingSession, { params: data }),
          })
        );
        return (resp as Record<string, Record<string, unknown>>)?.sessionStartSuccessResult?.machineOnboardingSessionId as string;
      }

      const data: StartMachineOnboardingSessionRequestDto = {
        endpoint: machine.endpoint as string | undefined,
        machineId: (machine.machineId as number) || null,
      };
      const resp = await dispatch(
        eventCall({
          connectionName: 'appHub',
          eventName: 'OnStartedMachineOnboardingSession',
          data: createApiCall((api) => api.machines.startMachineOnboardingSession, { params: data }),
        })
      );
      return (resp as Record<string, Record<string, unknown>>)?.sessionStartSuccessResult?.machineOnboardingSessionId as string;
    } catch (e) {
      return null;
    }
  };

export const validateMachineOnboardingEndpoint = (machine: Record<string, unknown>) => async (dispatch: AppDispatch) => {
  const data: ValidateMachineEndpointRequestDto = {
    machineOnboardingSessionId: machine.machineOnboardingSessionId as string,
  };

  try {
    const resp = await dispatch(
      eventCall({
        connectionName: 'appHub',
        eventName: 'OnValidatedMachineOnboarding',
        data: createApiCall((api) => api.machines.validateMachineOnboardingEndpoint, { params: data }),
      })
    );
    return resp;
  } catch (e) {
    return { error: true };
  }
};

export const validateMachineOnboardingByAutomaticControl = (machine: Record<string, unknown>) => async (dispatch: AppDispatch) => {
  const data: ValidateMachineByAutomaticControlRequestDto = {
    machineOnboardingSessionId: machine.machineOnboardingSessionId as string,
  };
  if (machine.machineCredentials) {
    data.machineCredentials = machine.machineCredentials as ValidateMachineByAutomaticControlRequestDto['machineCredentials'];
  }
  try {
    const resp = await dispatch(
      eventCall({
        connectionName: 'appHub',
        eventName: 'OnValidatedMachineOnboarding',
        data: createApiCall((api) => api.machines.validateMachineOnboardingByAutomaticControl, { params: data }),
      })
    );
    return resp;
  } catch (e) {
    return { error: true };
  }
};

export const validateMachineOnboardingByBrand = (machine: Record<string, unknown>) => async (dispatch: AppDispatch) => {
  const data: ValidateMachineByBrandRequestDto = {
    machineOnboardingSessionId: machine.machineOnboardingSessionId as string,
    machineBrandCode: machine.machineBrandCode as string,
  };
  if (machine.machineCredentials) {
    data.machineCredentials = machine.machineCredentials as ValidateMachineByBrandRequestDto['machineCredentials'];
  }
  try {
    const resp = await dispatch(
      eventCall({
        connectionName: 'appHub',
        eventName: 'OnValidatedMachineOnboarding',
        data: createApiCall((api) => api.machines.validateMachineOnboardingByBrand, { params: data }),
      })
    );
    return resp;
  } catch (e) {
    return { error: true };
  }
};

export const validateMachineOnboardingByConnectionMethod = (machine: Record<string, unknown>) => async (dispatch: AppDispatch) => {
  const data: ValidateMachineByConnectionMethodRequestDto = {
    machineOnboardingSessionId: machine.machineOnboardingSessionId as string,
    connectionMethodCode: machine.connectionMethodCode as string,
  };
  if (machine.machineCredentials) {
    data.machineCredentials = machine.machineCredentials as ValidateMachineByConnectionMethodRequestDto['machineCredentials'];
  }
  try {
    const resp = await dispatch(
      eventCall({
        connectionName: 'appHub',
        eventName: 'OnValidatedMachineOnboarding',
        data: createApiCall((api) => api.machines.validateMachineOnboardingByConnectionMethod, { params: data }),
      })
    );
    return resp;
  } catch (e) {
    return { error: true };
  }
};

export const addSelectedMachines = (): AppThunk => async (dispatch, getState) => {
  const {
    machines: { machineAddresses },
  } = getState();

  for (const index in machineAddresses) {
    const machine = machineAddresses[index] as Record<string, unknown>;
    if (!(machine as Record<string, unknown>).selected) {
      continue;
    }

    const data: Record<string, unknown> = {
      machineId: null,
      fullName: machine.fullName || machine.brandCode,
      shortName: machine.shortName || machine.fullName || machine.brandCode,
      ipAddress: machine.ipAddress,
      macAddress: machine.macAddress,
      machineBrand: {
        code: machine.brandCode,
      },
      machineControl: {
        code: machine.controlCode,
      },
      machineConnection: {
        connectionMethod: {
          code: machine.connectionMethodCode,
          defaultPort: machine.defaultPort,
        },
        switchedOn: true,
        ipAddress: machine.ipAddress,
        macAddress: machine.macAddress,
        allowNcFileTransfer: true,
        connectionMethodIsAutomatic: true,
      },
    };

    if (machine.userName && machine.password) {
      (data.machineConnection as Record<string, unknown>).user = machine.userName;
      (data.machineConnection as Record<string, unknown>).password = machine.password;
    }

    await dispatch(backendCall(createApiCall((api) => api.machines.configure, { params: { machine: data } })));
  }
};

const isPreviousTourStatsRequest = (date: string) => localTime(date).isBefore(localTime().subtract(2, 'days'));

export const getMachineStatsPerPeriod =
  (data: MachineStatisticsInput, signal?: AbortSignal): AppThunk<Promise<MachineStatisticPerPeriod[]>> =>
  async (dispatch, getState) => {
    if (isTourActive()) {
      const machineId = Number(data.machineId);
      if (isPreviousTourStatsRequest(data.date)) {
        const prevData = getDemoMachineStatsHourPrev(machineId);
        if (prevData) return prevData as MachineStatisticPerPeriod[];
      }
      const demoData = getDemoMachineStatsHour(machineId);
      if (demoData) return demoData as MachineStatisticPerPeriod[];
    }

    const {
      machinesV2: { selectedGroup },
    } = getState();
    const params: MachineStatisticsRequestDto = buildStatsGraphParams({
      ...data,
      machineGroupId: data.machineGroupId ?? (data.forAllMachines || data.allSelected ? selectedGroup?.machineGroupId : undefined),
    });

    const response = await dispatch(
      backendCall(
        createApiCall((api) => api.statistics.machineStatistics, {
          params,
          signal,
        })
      )
    );

    return response && 'machineStatistics' in response ? ((response.machineStatistics ?? []) as MachineStatisticPerPeriod[]) : [];
  };

export const getNcFileStatsMonth = (data: StatsParams) => async (dispatch: AppDispatch) => {
  return await dispatch(backendCall(createApiCall((api) => api.machines.ncFileStatsMonth, { params: data })));
};

export const getNcFileStatsDay = (data: StatsParams, signal?: AbortSignal) => async (dispatch: AppDispatch) => {
  return await dispatch(backendCall(createApiCall((api) => api.machines.ncFileStatsDay, { params: data, signal })));
};

export const getNcFileStatsHour = (data: StatsParams, signal?: AbortSignal) => async (dispatch: AppDispatch) => {
  return await dispatch(backendCall(createApiCall((api) => api.machines.ncFileStatsHour, { params: data, signal })));
};

export const getMachineEvent = (data: MachineEventParams, signal?: AbortSignal) => async (dispatch: AppDispatch) => {
  return await dispatch(backendCall(createApiCall((api) => api.machines.machineEvent, { params: data, signal })));
};

export const getMachineEventForBatchTimeline = (data: BatchTimelineParams, signal?: AbortSignal) => async (dispatch: AppDispatch) => {
  return await dispatch(backendCall(createApiCall((api) => api.machines.machineEventForBatchTimeline, { params: data, signal })));
};

export const getMachineFromHistory = (data: MachineHistoryParams) => async (dispatch: AppDispatch) => {
  return await dispatch(backendCall(createApiCall((api) => api.machines.getMachineFromHistory, { params: data })));
};

export const getDemoMachineType = () => async (dispatch: AppDispatch) => {
  return await dispatch(backendCall(createApiCall((api) => api.machines.demoMachineType, {})));
};

export const getMachineBrand = () => async (dispatch: AppDispatch) => {
  return await dispatch(backendCall(createApiCall((api) => api.machines.machineBrand, {})));
};

export const getMachineConnectionMethod = () => async (dispatch: AppDispatch) => {
  return await dispatch(backendCall(createApiCall((api) => api.machines.machineConnectionMethod, {})));
};

export const getMachineCountingProfiles = () => async (dispatch: AppDispatch) => {
  return await dispatch(backendCall(createApiCall((api) => api.machines.machineCountingProfiles, {})));
};

export const configureMachine = (data: ConfigureMachineRequestDto) => async (dispatch: AppDispatch) => {
  return await dispatch(backendCall(createApiCall((api) => api.machines.configure, { params: data })));
};

export const addMachine = (machine: Record<string, unknown>, isDemoMachine?: boolean) => async (dispatch: AppDispatch) => {
  try {
    if (machine.isDemoMachine) {
      const data: OnboardDemoMachineRequestDto = {
        machineOnboardingSessionId: machine.machineOnboardingSessionId as string,
      };
      const resp = await dispatch(
        eventCall({
          connectionName: 'appHub',
          eventName: 'OnOnboardedMachine',
          data: createApiCall((api) => api.machines.onboardDemoMachine, { params: data }),
        })
      );
      trackEvent(TRACKING_EVENTS.MACHINE_CONFIGURATION, {
        action: 'added',
        machineId: String(machine.machineId || resp?.machineId || ''),
        machineName: (machine.shortName || machine.fullName || machine.brandCode || '') as string,
        isDemoMachine: true,
        result: 'success',
      });
      return resp;
    }

    const data: OnboardMachineRequestDto = {
      machineOnboardingSessionId: machine.machineOnboardingSessionId as string,
      machineId: (machine.machineId as number) || null,
      fullName: (machine.fullName || machine.brandCode) as string,
      shortName: (machine.shortName || machine.fullName || machine.brandCode) as string,
    };
    const resp = await dispatch(
      eventCall({
        connectionName: 'appHub',
        eventName: 'OnOnboardedMachine',
        data: createApiCall((api) => api.machines.onboardMachine, { params: data }),
      })
    );
    trackEvent(TRACKING_EVENTS.MACHINE_CONFIGURATION, {
      action: 'added',
      machineId: String(machine.machineId || resp?.machineId || ''),
      machineName: (machine.shortName || machine.fullName || machine.brandCode || '') as string,
      machineBrand: (machine.machineBrand as Record<string, unknown>)?.name as string | undefined,
      connectionMethod: (machine.machineConnection as Record<string, unknown>)?.connectionMethod as string | undefined,
      isDemoMachine: !!machine.isDemoMachine,
      result: 'success',
    });
    return resp;
  } catch (e) {
    trackEvent(TRACKING_EVENTS.MACHINE_CONFIGURATION, {
      action: 'added',
      machineId: String(machine.machineId || ''),
      machineName: (machine.shortName || machine.fullName || machine.brandCode || '') as string,
      isDemoMachine: !!machine.isDemoMachine,
      result: 'failed',
    });
    return { error: true };
  }
};

export const updateMachinePI =
  (playload: Record<string, unknown>): AppThunk =>
  (dispatch, getState) => {
    const {
      machines: { selectedItem, machines },
    } = getState();

    if (
      selectedItem?.machineStatus?.status === machineStatuses.NOTPRODUCTIVE &&
      selectedItem?.machineStatus?.ncFileStatus === ncFileStatuses.Unknown
    ) {
      const base = (i: Record<string, unknown>) => {
        return {
          machineStatus: {
            ...(i.machineStatus as Record<string, unknown>),
            status: machineStatuses.ACTIVE,
            ncFileStatus: ncFileStatuses.Linked,
          },
          isProcessingPI: true,
          partDetails: playload.part || {},
          clampingDetails: playload.clampingDetails || {},
        };
      };

      dispatch(
        setSelectedMachine(
          JSON.parse(
            JSON.stringify(
              mapMachine({
                ...selectedItem,
                ...base,
              } as Partial<Machine>)
            )
          )
        )
      );

      const newData = JSON.parse(JSON.stringify(machines));

      const array = newData.map((item: Record<string, unknown>) => {
        if (item.id === selectedItem.id) {
          return {
            ...item,
            ...base(item),
          };
        }
        return item;
      });
      dispatch(setMachines(array));
    }
  };

export const getMachine =
  (id: number | string | null): AppThunk<Promise<Partial<Machine> | null>> =>
  async (dispatch, getState) => {
    const {
      machines: { machines = [] },
    } = getState();

    if (!id) {
      return null;
    }

    const exist = machines.find((m: Machine) => id == m.machineId);

    if (exist) {
      return mapMachine(exist);
    }

    const result = await dispatch(
      backendCall(
        createApiCall((api) => api.machines.search, {
          params: { id: Number(id) },
          onSuccess: async () => {},
          onError: () => {},
        })
      )
    );
    const machine = (result as unknown[])?.[0];
    return mapMachine(machine as Partial<Machine>);
  };

let machineStore: Record<number, { machineStatus: Record<string, unknown> }> = {};
let NCHashStore: Record<number, unknown> = {};

export const setDataToMachineStore = (data: Record<string, unknown>) => {
  const existing = machineStore[data.machineId as number]?.machineStatus || {};
  machineStore[data.machineId as number] = {
    machineStatus: {
      ...existing,
      ...data,
    },
  };
};

export const setDataToNCHashStore = (data: Record<string, unknown>) => (dispatch: AppDispatch) => {
  if (data?.ncFileId) {
    NCHashStore[data.ncFileId as number] = data.ncFileContentHash;
  }
};

export const prepearMachine = (item: Machine): Machine => {
  const machineStatus = item.machineStatus ? { ...item.machineStatus } : {};

  if (!machineStatus.status) {
    machineStatus.status = machineStatuses.NOTPRODUCTIVE;
  }

  if (!machineStatus.partDetails) {
    machineStatus.partDetails = {};
  }

  return {
    ...item,
    id: item.machineId,
    machineStatus,
    extendedData: item.extendedData ? item.extendedData : {},
    cycleTime: item.cycleTime ? item.cycleTime : {},
    machineConnection: item.machineConnection ? item.machineConnection : {},
    productionOrderDetails: item.productionOrderDetails ? item.productionOrderDetails : {},
  };
};

export const croppedMachineImage =
  (data: Record<string, unknown>): AppThunk =>
  (dispatch, getState) => {
    const {
      machines: { machines },
    } = getState();

    machines.forEach((m: Machine) => {
      if (
        data?.documentId === m.machineStatus?.imageDocumentId &&
        (data?.cropSettings as Record<string, unknown>)?.pageNumber === m.machineStatus?.imageData?.pageNumber
      ) {
        setDataToMachineStore({
          machineId: m.machineId,
          ...(m.machineStatus as Record<string, unknown>),
          imageData: {
            ...(m.machineStatus?.imageData || {}),
            isCropped: true,
          },
          imageRandomHash: Math.round(Math.random() * 100),
        } as Record<string, unknown>);
      }
    });
  };

export const rotatedMachineImage =
  (data: Record<string, unknown> = {}): AppThunk =>
  (dispatch, getState) => {
    const {
      machines: { machines },
    } = getState();

    const base = (data.cropSettings || data.pageDetails || {}) as Record<string, unknown>;

    machines.forEach((m: Machine) => {
      if (data?.documentId === m.machineStatus?.imageDocumentId && base.pageNumber === m.machineStatus?.imageData?.pageNumber) {
        setDataToMachineStore({
          machineId: m.machineId,
          ...(m.machineStatus as Record<string, unknown>),
          imageDetails: ((m.machineStatus?.imageDetails as unknown[]) || []).map((i: unknown) => {
            if ((i as Record<string, unknown>).pageNumber === base.pageNumber) {
              return base;
            }
            return i;
          }),
          imageRandomHash: Math.round(Math.random() * 100),
        } as Record<string, unknown>);
      }
    });
  };

export const updateMachineInfo = (): AppThunk => (dispatch, getState) => {
  // Skip real updates during tour — demo data is static
  if (isTourActive()) {
    return;
  }

  const {
    machines: { machines, selectedItem },
  } = getState();

  if (!machines || !machines.length || !Object.keys(machineStore).length) {
    return;
  }

  const isProcessing = (item: Machine) => {
    if (item.isProcessing && item?.machineStatus?.ncFileId === machineStore[item.machineId!].machineStatus.ncFileId) {
      return true;
    }
    return false;
  };

  const newData: Machine[] = JSON.parse(JSON.stringify(machines));

  const array = newData.map((item) => {
    if (machineStore[item.machineId!]) {
      item = {
        ...item,
        oldMachineStatus: item.machineStatus,
        oldMachineStatusDate: new Date().getTime(),
        isProcessing: isProcessing(item),
        isProcessingPI: false,
        machineStatus: {
          ...(item.machineStatus as Record<string, unknown>),
          ...machineStore[item.machineId!].machineStatus,
        },
      };
    }

    if (NCHashStore[item.machineStatus?.ncFileId as number]) {
      item = {
        ...item,
        machineStatus: {
          ...(item.machineStatus as Record<string, unknown>),
          ncFileContentHash: NCHashStore[item.machineStatus?.ncFileId as number] as string,
        },
      };
    }

    let prepeared = item;
    if (machineStore[item.machineId!] || NCHashStore[item.machineStatus?.ncFileId as number]) {
      prepeared = prepearMachine(item);
    }

    return prepeared;
  });

  let newSelected: Machine = JSON.parse(JSON.stringify(selectedItem));

  if (machineStore[newSelected.machineId!]) {
    newSelected = {
      ...newSelected,
      isProcessing: isProcessing(newSelected),
      isProcessingPI: false,
      machineStatus: {
        ...(newSelected.machineStatus as Record<string, unknown>),
        ...machineStore[newSelected.machineId!].machineStatus,
      },
    };
  }

  if (NCHashStore[newSelected.machineStatus?.ncFileId as number]) {
    newSelected = {
      ...newSelected,
      machineStatus: {
        ...(newSelected.machineStatus as Record<string, unknown>),
        ncFileContentHash: NCHashStore[newSelected.machineStatus?.ncFileId as number] as string,
      },
    };
  }

  if (machineStore[newSelected.machineId!] || NCHashStore[newSelected.machineStatus?.ncFileId as number]) {
    dispatch(setSelectedMachine(prepearMachine(newSelected)));
  }

  machineStore = {};
  NCHashStore = {};

  dispatch(setMachines(array));
};

export const finishProcessingMachineImage =
  ({ imageHash, image }: { imageHash: string; image: unknown }): AppThunk =>
  (dispatch, getState) => {
    const {
      machines: { machines },
    } = getState();
    if (!machines || !machines.length) {
      return;
    }

    let changeDetected = false;
    const machinesList = machines.map((m: Machine) => {
      if (imageHash === (m as unknown as Record<string, unknown>).processingID) {
        changeDetected = true;
        return {
          ...m,
          isProcessing: false,
          processingID: null,
        } as Machine;
      }
      return m;
    });
    if (changeDetected) {
      dispatch(setMachines(machinesList));
    }
  };

export const getMachinesGroup = () => async (dispatch: AppDispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.machines.getMachinesGroup, {
        onSuccess: async (data) => {
          dispatch(setMachinesGroup(data));
        },
      })
    )
  );
};

export const getMachinesReferencesBySourcesSystem = (sourceSystem: SourceSystem) => async (dispatch: AppDispatch) => {
  if (!sourceSystem) return;

  await dispatch(
    backendCall(
      createApiCall((api) => api.machines.getMachinesReferencesBySourcesSystem, {
        params: { sourceSystem },
        onSuccess: (data) => {
          dispatch(setMachinesReferences(data?.machineReferences ?? []));
        },
        onError: (e: unknown) => {
          console.error(e);
          Alert({ title: i18n.t('Error'), text: i18n.t('Something didn’t work') });
        },
      })
    )
  );
};

export const configureMachineReferences =
  ({ signal, ...params }: ConfigureMachineReferencesParams): AppThunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    if (!params) return;

    const { action, machineId, machineReference } = params;

    const { machinesReferences } = getState()?.machines ?? {};

    if (!machinesReferences) return;

    dispatch(setMachinesReferencesLoaded(false));

    const machinesAndReferencesToUnlink = new Map<Record<string, unknown>, unknown[]>();

    if (action === MACHINE_REFERENCE_ACTION.LINK) {
      const currentReference = machinesReferences.find((mr: MachineReference) => mr.name === (machineReference as Record<string, unknown>).name);

      const machinesLinkedToCurrentReferenceExceptCurrentMachine = (currentReference?.machines ?? [])
        .filter((m: Machine2) => m?.machineId !== machineId)
        .map((m: Machine2) => m?.machineId);

      const { machines, ...currentReferenceWithoutMachines } = currentReference as MachineReference;

      machinesAndReferencesToUnlink.set(
        currentReferenceWithoutMachines as Record<string, unknown>,
        machinesLinkedToCurrentReferenceExceptCurrentMachine as unknown[]
      );

      const referencesWithLinkedCurrentMachine = machinesReferences
        .filter(
          (mr: MachineReference) => mr.name !== currentReference?.name && (mr?.machines ?? []).some((m: Machine2) => m?.machineId === machineId)
        )
        .map(({ machines, ...rest }: MachineReference) => rest);

      referencesWithLinkedCurrentMachine.forEach((r: Omit<MachineReference, 'machines'>) => {
        machinesAndReferencesToUnlink.set(r, [machineId]);
      });
    }

    const onError = (e: Record<string, unknown> | undefined) => {
      dispatch(setMachinesReferencesLoaded(true));
      console.error(e);
      Alert({ title: (e?.errorMessage as string) || 'Error' });
    };

    const onSuccess = async (response: ConfigureMachineReferencesResponse) => {
      if (response.isError) {
        dispatch(setMachinesReferencesLoaded(true));
        console.error(response.errorMessage);
        Alert({ title: (response?.errorMessage as string) || 'Error' });
        return;
      }

      await dispatch(getMachinesReferencesBySourcesSystem(SourceSystems.Imnoo as SourceSystem));
      Alert({ title: 'Success' });
    };

    const linkActions: Record<string, unknown>[] = [{ ...params }];

    if (machinesAndReferencesToUnlink.size > 0) {
      const referencesToUnlink = Array.from(machinesAndReferencesToUnlink.keys());
      referencesToUnlink.forEach((r: Record<string, unknown>) => {
        const machinesToUnlink = machinesAndReferencesToUnlink.get(r);
        (machinesToUnlink as unknown[]).forEach((m) => {
          linkActions.push({ action: MACHINE_REFERENCE_ACTION.UNLINK, machineId: m, machineReference: r });
        });
      });
    }

    return await dispatch(
      backendCall(
        createApiCall((api) => api.machines.configureMachineReferences, {
          params: { linkActions } as ConfigureMachineReferencesRequestDto,
          onSuccess,
          onError,
          signal,
        })
      )
    );
  };

export const getMachinesOperations =
  (params: OperationsParams = {}) =>
  async (dispatch: AppDispatch) => {
    return await dispatch(
      backendCall(
        createApiCall((api) => api.machines.getMachinesOperations, {
          params,
          onSuccess: async (data) => {
            dispatch(setMachinesOperations(data));
          },
        })
      )
    );
  };

export const getMachineOperationById =
  (id: number) =>
  async (dispatch: AppDispatch): Promise<Operation | undefined> => {
    const data = (await dispatch(
      backendCall(
        createApiCall((api) => api.machines.getMachinesOperations, {
          params: { filter: `operationId eq ${id}` },
        })
      )
    )) as Operation[] | undefined;
    const found = data?.find((d: Operation) => d.operationId === Number(id));
    if (found) {
      dispatch(upsertMachinesOperation(found));
    }
    return found;
  };

export const configureGroup = (data: ConfigureMachineGroupRequestDto, success?: () => void) => async (dispatch: AppDispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.machines.configureGroup, {
        params: data,
        onSuccess: async (data) => {
          if (data.failureDetails) {
            Alert({
              title: i18n.t(data.failureDetails.errorMessage || 'Error'),
              type: 'danger',
            });
          }
          if (data.successDetails) {
            if (success) {
              success();
              dispatch(getMachinesGroup());
            }
          }
        },
      })
    )
  );
};

export const configureOperations =
  ({ signal, ...data }: ConfigureOperationsParams, success?: () => void) =>
  async (dispatch: AppDispatch) => {
    dispatch(setMachinesOperationsLoaded(false));

    const response = await dispatch(
      backendCall(
        createApiCall((api) => api.machines.configureOperations, {
          params: data as ConfigureOperationRequestDto,
          onSuccess: async (data) => {
            if (data.failureDetails) {
              Alert({
                title: i18n.t(data.failureDetails.errorMessage || 'Error'),
                type: 'danger',
              });
            }
            if (data.successDetails) {
              Alert({ title: 'Success' });
              success?.();
              if (data.successDetails.operationId != null) {
                await dispatch(getMachineOperationById(data.successDetails.operationId));
              }
            }
          },
          signal,
        })
      )
    );

    dispatch(setMachinesOperationsLoaded(true));

    return response;
  };

export const getMachineById = (id: number) => async (dispatch: AppDispatch) => {
  const result = await dispatch(backendCall(createApiCall((api) => api.machines.getMachineById, { params: { id } })));
  const machines = (result as Machine[])?.map((item) => prepearMachine(item));
  const first = machines?.[0];
  if (first) {
    dispatch(setInspectedMachine(first));
  }
  return machines;
};

export const refreshMachineImageAfterUpload =
  ({ machineId, optimisticImage }: { machineId: number; optimisticImage: NonNullable<Machine['machineImage']> }): AppThunk<Promise<void>> =>
  async (dispatch) => {
    dispatch(setMachineImage({ machineId, machineImage: optimisticImage }));
    const fetched = await dispatch(getMachineById(machineId));
    const fetchedMachine = fetched?.[0];
    if (fetchedMachine) {
      dispatch(replaceMachine(fetchedMachine));
    }
  };

const matchesAnyIndirectLink = (
  machineStatus: NonNullable<Machine['machineStatus']>,
  links: NonNullable<Document['links']>
): boolean =>
  links.some((link) => {
    if (link?.scopeId === KEYS_TAGS.Clamping) {
      return link.scopeReferenceId != null && link.scopeReferenceId === machineStatus.ncFileId;
    }
    if (link?.scopeId === KEYS_TAGS.Part) {
      return link.scopeReferenceId != null && link.scopeReferenceId === machineStatus.partDetails?.partId;
    }
    return false;
  });

export const refreshMachineImagesOnUpload =
  ({ links, machineImage }: { links: NonNullable<Document['links']>; machineImage: NonNullable<Machine['machineImage']> }): AppThunk =>
  (dispatch) => {
    const machineLink = links.find((l) => l?.scopeId === KEYS_TAGS.Machine && typeof l?.scopeReferenceId === 'number');
    const directMachineId = machineLink?.scopeReferenceId as number | undefined;
    if (directMachineId != null) {
      dispatch(setMachineImage({ machineId: directMachineId, machineImage }));
    }

    const indirectLinks = links.filter((l) => l?.scopeId != null && INDIRECT_MACHINE_LINK_SCOPE_IDS.includes(l.scopeId));
    if (indirectLinks.length) {
      dispatch(applyMachineImageByIndirectLinks({ links: indirectLinks, machineImage }));
    }
  };

const buildLocalOptimisticImage = ({
  tempID,
  photo,
  file,
}: {
  tempID: string;
  photo: any;
  file: any;
}): NonNullable<Machine['machineImage']> => {
  const mimeType: string = file?.type || 'image/jpeg';
  const base64 = photo?.base64 ?? file?.base64;
  const localUri = base64 ? `data:${mimeType};base64,${base64}` : photo?.uri ?? file?.uri;
  return {
    documentId: tempID,
    url: localUri,
    urlSmall: localUri,
    originUrl: localUri,
    local: true,
  } as unknown as NonNullable<Machine['machineImage']>;
};

export const setOptimisticMachineImageFromUpload =
  ({ machineId, tempID, photo, file }: { machineId: number; tempID: string; photo: any; file: any }) =>
  (dispatch: AppDispatch) => {
    dispatch(setMachineImage({ machineId, machineImage: buildLocalOptimisticImage({ tempID, photo, file }) }));
  };

export const setOptimisticMachineImageOnUpload =
  ({
    tags,
    tempID,
    photo,
    file,
  }: {
    tags: NonNullable<Document['links']>;
    tempID: string;
    photo: any;
    file: any;
  }): AppThunk =>
  (dispatch) => {
    const optimisticImage = buildLocalOptimisticImage({ tempID, photo, file });

    const machineTag = tags.find((t) => Number(t?.scopeId) === KEYS_TAGS.Machine);
    const directMachineId = Number(machineTag?.scopeReferenceId);
    if (machineTag && Number.isFinite(directMachineId)) {
      dispatch(setMachineImage({ machineId: directMachineId, machineImage: optimisticImage }));
    }

    const indirectTags = tags.filter((t) => t?.scopeId != null && INDIRECT_MACHINE_LINK_SCOPE_IDS.includes(Number(t.scopeId)));
    if (indirectTags.length) {
      dispatch(applyMachineImageByIndirectLinks({ links: indirectTags, machineImage: optimisticImage }));
    }
  };

export const getMachines =
  (formatResult = false, preselectedId?: number | string): AppThunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    // Skip real API calls during tour — demo data is already in Redux
    if (isTourActive()) {
      return;
    }

    const result = await dispatch(
      backendCall(
        createApiCall((api) => api.machines.search, {
          saveDuration: setRequestTimeEnd,
          onSuccess: async (data) => {
            const {
              machines: { selectedItem },
            } = getState();
            let exist = false;
            const renderData = data.map((item) => {
              const m = prepearMachine(item);

              // Check if this machine matches the currently selected machine
              const isSelectedMachine = selectedItem?.machineId === m.machineId || selectedItem?.id === m.id;

              if (isSelectedMachine) {
                exist = true;

                // Get current URL to verify this is still the intended selection
                const urlMachineId = navigationRef?.current?.getCurrentRoute()?.params?.id;
                const currentMachineId = urlMachineId ? Number(urlMachineId) : null;

                // Only refresh if this machine matches the current URL parameter
                if (currentMachineId === m.id) {
                  dispatch(setSelectedMachine(m));
                }
              }
              return m;
            });

            if (preselectedId) {
              // Get current URL to verify preselection is still valid
              const urlMachineId = navigationRef?.current?.getCurrentRoute()?.params?.id;
              const currentMachineId = urlMachineId ? Number(urlMachineId) : null;

              if (String(preselectedId).toLowerCase() == ALL_MACHINES) {
                dispatch(
                  setSelectedMachine({
                    id: ALL_MACHINES,
                    fullName: i18n.t('All Machines'),
                    shortName: i18n.t('All Machines'),
                    allSelected: true,
                    machineStatus: {
                      status: machineStatuses.ACTIVE,
                    },
                  } as Partial<Machine>)
                );
              } else {
                // Apply preselection when the URL already matches OR when the URL has no id yet
                // (e.g. fresh app start with a persisted selectedItem) — the dispatch is what syncs
                // the id back into the URL via setSelectedMachine's reducer side-effect.
                if (currentMachineId === Number(preselectedId) || currentMachineId === null) {
                  const exist = renderData.find((i) => i.id == preselectedId);
                  if (exist) {
                    dispatch(setSelectedMachine(exist));
                  }
                }
              }
            }

            dispatch(setMachines(renderData));

            // Enrich Amplitude user properties with machine data (deferred from login)
            const { membership: { isWithERP } = {} } = getState() as { membership?: { isWithERP?: boolean } };
            updateUserProperties({
              machineCount: renderData.length,
              demoMachineCount: renderData.filter((m: Record<string, unknown>) => (m.demoMachineTypeId as number) > 0).length,
              hasERPIntegration: !!isWithERP,
            });

            return renderData;
          },
          onError: (error: unknown) => {
            console.log(error, 'error');
            dispatch(setMachinesError());
          },
        })
      )
    );
    if (result) {
      dispatch(refreshInfo((result as Record<string, unknown>).refreshInfo as Record<string, unknown>));
    } else {
      return [];
    }

    return formatResult ? (result as Machine[])?.map((item) => prepearMachine(item)) : result;
  };

export const getNCFileStatistics =
  ({ period, date, machineId, reset, numberOfWeeks }: NCFileStatisticsParams, signal?: AbortSignal): AppThunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    if (isTourActive()) return;

    const {
      machines: { ncFileStatistics },
    } = getState();

    let page = ncFileStatistics.page || 1;
    const count = ncFileStatistics.count || 100;
    const data = ncFileStatistics.data || [];

    const save = (d: NcFileStatisticsData) => dispatch(setNCFileStatistics(d));
    if (reset) {
      page = 1;
      save({ data: [], page: 1 });
    }

    const normalizedMachineId = parseRouteParamAsNumber(machineId);
    const params: NcFileStatisticsRequest2Dto = {
      period,
      date,
      ...injectConditionalObject(typeof normalizedMachineId === 'number', { machineId: normalizedMachineId }),
      ...injectConditionalObject(!!numberOfWeeks, { numberOfWeeks }),
      pagination: {
        pageNumber: page,
        pageSize: count,
        includeCount: true,
      },
    };

    return dispatch(
      backendCall(
        createApiCall((api) => api.statistics.ncFileStatistics, {
          signal,
          onSuccess: async (d) => {
            const isMore = ((d as Record<string, unknown>).machineStatistics as unknown[]).length === count;
            let maxValueActive = 0;
            let maxValueProductive = 0;

            if (reset) {
              ((d as Record<string, unknown>).machineStatistics as Array<Record<string, number>>).forEach((i) => {
                maxValueActive = Math.max(maxValueActive, i.totalActiveSecs, i.totalActiveSecsPrevious);
                maxValueProductive = Math.max(maxValueProductive, i.totalProductiveSecs, i.totalProductiveSecsPrevious);
              });
              save({ data: (d as Record<string, unknown>).machineStatistics, page: page + 1, isMore, maxValueActive, maxValueProductive });
            } else {
              [...data, ...((d as Record<string, unknown>).machineStatistics as Array<Record<string, number>>)].forEach(
                (i: Record<string, number>) => {
                  maxValueActive = Math.max(maxValueActive, i.totalActiveSecs, i.totalActiveSecsPrevious);
                  maxValueProductive = Math.max(maxValueProductive, i.totalProductiveSecs, i.totalProductiveSecsPrevious);
                }
              );
              save({
                data: [...data, ...((d as Record<string, unknown>).machineStatistics as unknown[])],
                page: page + 1,
                isMore,
                maxValueActive,
                maxValueProductive,
              });
            }
          },
          params,
        })
      )
    );
  };

export const getMachineStatistics =
  ({ period, date, numberOfWeeks }: MachineStatisticsParams, signal?: AbortSignal): AppThunk =>
  async (dispatch, getState) => {
    if (isTourActive()) return;

    const {
      machines: { machines, selectedItem, machineStatistics },
      machinesV2: { selectedGroup },
    } = getState();

    dispatch(
      setMachineStatistics({
        data: machineStatistics.data,
        loaded: false,
      })
    );

    if (period === 'now') {
      return;
    }

    const params: MachineStatisticsRequestDto = buildStatsListParams({
      period,
      date,
      numberOfWeeks,
      machineGroupId: selectedGroup?.machineGroupId,
    });

    dispatch(
      backendCall(
        createApiCall((api) => api.statistics.machineStatistics, {
          params,
          signal,
          onSuccess: async (data: MachineStatisticsResponse) => {
            let maxValueActive = 0;
            let maxValueProductive = 0;

            const storage: Record<number, Machine> = {};
            machines.forEach((i) => {
              storage[i.machineId as number] = i;
            });

            const machineStatisticsResult: Record<string, unknown>[] = [];

            const processedIds = new Set<number>();

            const statistics = ('machineStatistics' in data ? data.machineStatistics ?? [] : []) as MachineStatistic[];
            statistics.forEach((i: MachineStatistic) => {
              maxValueActive = Math.max(maxValueActive, i.totalActiveSecs ?? 0, i.totalActiveSecsPrevious ?? 0);
              maxValueProductive = Math.max(maxValueProductive, i.totalProductiveSecs ?? 0, i.totalProductiveSecsPrevious ?? 0);

              const id = i.machineId;
              if (id == null) return;
              processedIds.add(id);
              const machine = storage[id];
              if (machine) {
                machineStatisticsResult.push({
                  ...machine,
                  machineStatistics: {
                    ...i,
                    totalLoaded: true,
                  },
                });
                return;
              }
              machineStatisticsResult.push({
                ...i,
                id,
                machineStatus: {
                  status: 'Active',
                },
                machineStatisticsOnly: true,
                machineStatistics: {
                  ...i,
                  totalLoaded: true,
                },
              });
            });

            // add machines that have no data in the selected period
            Object.values(storage).forEach((machine) => {
              if (!processedIds.has(machine.machineId as number)) {
                machineStatisticsResult.push({
                  ...machine,
                  hasNoDataInPeriod: true,
                  machineStatistics: null,
                });
              }
            });

            if (selectedItem.needUpdateAfterLoad) {
              // Get current URL to check if this machine is still the intended selection
              const urlMachineId = navigationRef?.current?.getCurrentRoute()?.params?.id;
              const currentMachineId = urlMachineId ? Number(urlMachineId) : null;

              // Only restore if this machine matches the current URL parameter
              if (currentMachineId === selectedItem.id) {
                const exist = machineStatisticsResult.find((i) => i.id === selectedItem.id);
                if (exist) {
                  dispatch(setSelectedMachine({ ...exist, needUpdateAfterLoad: false } as Partial<Machine>));
                }
              }
            }

            dispatch(
              setMachineStatistics({
                data: machineStatisticsResult,
                maxValueActive,
                maxValueProductive,
                loaded: true,
              })
            );
          },
          onError: (error: unknown) => {
            console.log(error, 'error');
          },
        })
      )
    );
  };

export const getMachineStatisticsForNCFile =
  ({ period, date, ncFileId, numberOfWeeks }: MachineStatisticsForNCFileParams, signal?: AbortSignal) =>
  async (dispatch: AppDispatch) => {
    dispatch(
      setMachineStatisticsForNCFile({
        data: {},
      })
    );

    if (period === 'now') {
      return;
    }

    const params: MachineStatisticsRequestDto = buildStatsListParams({
      period,
      date,
      ncFileId,
      numberOfWeeks,
    });

    await dispatch(
      backendCall(
        createApiCall((api) => api.statistics.machineStatistics, {
          params,
          signal,
          onSuccess: async (data: MachineStatisticsResponse) => {
            let maxValueActive = 0;
            let maxValueProductive = 0;

            const statistics = ('machineStatistics' in data ? data.machineStatistics ?? [] : []) as MachineStatistic[];
            statistics.forEach((i: MachineStatistic) => {
              maxValueActive = Math.max(maxValueActive, i.totalActiveSecs ?? 0, i.totalActiveSecsPrevious ?? 0);
              maxValueProductive = Math.max(maxValueProductive, i.totalProductiveSecs ?? 0, i.totalProductiveSecsPrevious ?? 0);
            });

            dispatch(
              setMachineStatisticsForNCFile({
                data: statistics,
                maxValueActive,
                maxValueProductive,
              })
            );
          },
          onError: (error: unknown) => {
            console.log(error, 'error');
          },
        })
      )
    );
  };

export const deleteMachine =
  (id: number): AppThunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    const state = getState();
    const {
      machines: { machines, selectedItem },
    } = state;

    return await dispatch(
      backendCall(
        createApiCall((api) => api.machines.deleteMachine, {
          onSuccess: () => {
            const deletedMachine = machines.find((m) => m.id === id);
            trackEvent(TRACKING_EVENTS.MACHINE_CONFIGURATION, {
              action: 'deleted',
              machineId: String(id),
              machineName: deletedMachine?.shortName || deletedMachine?.fullName || '',
              result: 'success',
            });
            const newMachines = machines.filter((m) => m.id !== id);
            dispatch(setMachines(newMachines));
            if (id === selectedItem.id) {
              dispatch(setSelectedMachine({}));
            }
          },
          params: { machineId: id },
          onError: (error: unknown) => {
            trackEvent(TRACKING_EVENTS.MACHINE_CONFIGURATION, {
              action: 'deleted',
              machineId: String(id),
              machineName: '',
              result: 'failed',
            });
            console.log(error, 'error');
          },
        })
      )
    );
  };

export const switchConnection = (data: SwitchMachineConnectionRequestDto) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const {
    machines: { machines: machinesList },
  } = getState();
  const matchedMachine = machinesList.find((m) => m.machineId === data.machineId);
  return await dispatch(
    backendCall(
      createApiCall((api) => api.machines.switchConnection, {
        onSuccess: () => {
          trackEvent(TRACKING_EVENTS.MACHINE_CONFIGURATION, {
            action: 'connectionToggled',
            machineId: String(data.machineId || ''),
            machineName: matchedMachine?.shortName || matchedMachine?.fullName || '',
            connectionEnabled: !!data.isConnectionEnabled,
            wasConnected: !data.isConnectionEnabled,
            result: 'success',
          });
        },
        params: data,
        onError: (error: unknown) => {
          trackEvent(TRACKING_EVENTS.MACHINE_CONFIGURATION, {
            action: 'connectionToggled',
            machineId: String(data.machineId || ''),
            machineName: matchedMachine?.shortName || matchedMachine?.fullName || '',
            connectionEnabled: !!data.isConnectionEnabled,
            wasConnected: !data.isConnectionEnabled,
            result: 'failed',
          });
          console.log(error, 'error');
        },
      })
    )
  );
};

export const updateMachine =
  (data: Record<string, unknown>, storeData: Record<string, unknown> | false = false): AppThunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    const state = getState();
    const {
      machines: { machines, selectedItem },
    } = state;

    const reducerData = storeData || data;

    const oldMachine = machines.find((m) => m.machineId === reducerData.machineId);
    const changedFields = oldMachine
      ? Object.keys(reducerData).filter(
          (k) => k !== 'machineId' && reducerData[k] !== (oldMachine as Record<string, unknown>)[k] && typeof reducerData[k] !== 'object'
        )
      : [];

    return await dispatch(
      backendCall(
        createApiCall((api) => api.machines.configure, {
          onSuccess: () => {
            trackEvent(TRACKING_EVENTS.MACHINE_CONFIGURATION, {
              action: 'updated',
              machineId: String(reducerData.machineId || ''),
              machineName: (oldMachine?.shortName || oldMachine?.fullName || reducerData.shortName || '') as string,
              fieldName: changedFields[0] || '',
              oldValue: changedFields[0] ? String((oldMachine as Record<string, unknown>)?.[changedFields[0]] ?? '') : '',
              newValue: changedFields[0] ? String(reducerData[changedFields[0]] ?? '') : '',
              result: 'success',
            });
            const newMachines = machines.map((machine) => {
              if (machine.machineId === reducerData.machineId) {
                return {
                  ...machine,
                  ...reducerData,
                };
              }
              return machine;
            });
            const renderData = newMachines.map((item) => prepearMachine(item));

            dispatch(setMachines(renderData));

            if (selectedItem.machineId === reducerData.machineId) {
              const selected = renderData.find((machine) => machine.machineId === reducerData.machineId);
              dispatch(setSelectedMachine(selected as Machine));
            }
          },
          params: data,
          onError: (error: unknown) => {
            trackEvent(TRACKING_EVENTS.MACHINE_CONFIGURATION, {
              action: 'updated',
              machineId: String(reducerData.machineId || ''),
              machineName: (reducerData.shortName || reducerData.fullName || '') as string,
              fieldName: '',
              oldValue: '',
              newValue: '',
              result: 'failed',
            });
            console.log(error, 'error');
          },
        })
      )
    );
  };

export const storedMachine =
  (data: Record<string, unknown>): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const {
      machines: { machines },
    } = state;

    const obj = machines.find((machine) => machine.machineId === data.machineId);
    if (obj) {
      //update
      let newMachines = machines.map((machine) => {
        if (machine.machineId === data.machineId) {
          return {
            ...machine,
            ...data,
          };
        }
        return machine;
      });
      let renderData1 = newMachines.map((item) => prepearMachine(item));
      dispatch(setMachines(renderData1));
    } else {
      //add
      let newMachineData = [...machines, data];
      let renderData2 = newMachineData.map((item) => prepearMachine(item));
      dispatch(setMachines(renderData2));
    }
  };

export const switchMachine =
  (action: string): AppThunk =>
  async (dispatch, getState) => {
    const {
      machines: { machines, selectedItem },
    } = getState();

    const actions = {
      UP: 'up',
      DOWN: 'down',
    };

    let indexCurrent = 0;
    machines.some((m, index) => {
      if (m.machineId === selectedItem.machineId) {
        indexCurrent = index;
        return true;
      }
    });
    const lastIndex = machines.length - 1;

    const update = (m: Machine) => {
      dispatch(setSelectedMachine(m));
    };

    if (actions.UP === action) {
      if (indexCurrent === 0) {
        return update(machines[lastIndex]);
      }
      return update(machines[indexCurrent - 1]);
    }

    if (actions.DOWN === action) {
      if (indexCurrent === lastIndex) {
        return update(machines[0]);
      }
      return update(machines[indexCurrent + 1]);
    }
  };

export const getNCFile =
  (ncFileContentHash: string, fullFile = false) =>
  async (dispatch: AppDispatch) => {
    // Tour demo data: return cached content instead of fetching from blob storage
    const demoContent = getDemoNcFileContent(ncFileContentHash);
    if (demoContent) {
      return { data: demoContent, size: demoContent.length };
    }

    try {
      const url = await dispatch(generateNCFileURL({ ncFileContentHash, fullFile }));
      let d: Response | Record<string, unknown>;
      if (isWeb) {
        d = await fetch(url, { ignoreInterceptor: 'true' } as RequestInit);
      } else {
        d = await ReactNativeBlobUtil.config({ fileCache: true }).fetch('GET', url, { ignoreInterceptor: 'true' });
      }

      let length: string | null;

      if (isWeb) {
        length = (d as Response).headers.get('content-length');
      } else {
        length = (d as Record<string, Record<string, Record<string, string>>>).respInfo.headers['"Content-Length"'];
      }

      const text = await (d as Response).text();
      if (text.indexOf('<Error>') > -1) {
        return { isBlobError: true };
      }
      return { data: text, size: Number(length) };
    } catch (e) {
      console.log(e, 'Error get NC file');
      return { isError: true };
    }
  };

export const updateWallboardEventTimeline =
  ({ id }: WallboardEventTimelineParams): AppThunk =>
  async (dispatch, getState) => {
    const {
      machines: { wallboardTimelineStorage },
    } = getState();

    const events = wallboardTimelineStorage[id];

    if (!events?.length) {
      return dispatch(getWallboardEventTimeline({ id }));
    }

    const start = events[events.length - 1].endedOn;
    const end = localTime().utc().format();

    const params = {
      machineId: id,
      from: start,
      until: end,
      pagination: {
        pageNumber: 1,
        pageSize: 1000,
        includeCount: true,
      },
    };

    const resp = await dispatch(backendCall(createApiCall((api) => api.manufacture.machineEventTimeline, { params })));
    const data = (resp as Record<string, unknown>).machineEvents as Array<Record<string, unknown>>;

    const filtered = data.reduce((accumulator: Record<string, unknown>[], item, index) => {
      if (index === 0) {
        item.startedOn = start;
      }

      return [
        ...accumulator,
        {
          ...item,
          durationInSeconds: moment.utc(item.endedOn as string).diff(moment.utc(item.startedOn as string), 'seconds'),
        },
      ];
    }, []);

    const storage = [...events, ...filtered] as Array<Record<string, unknown>>;

    dispatch(
      saveWallboardEventTimeline({
        data: storage.filter((i, index) => {
          const prev = storage[index - 1];
          if (prev && prev.startedOn === i.startedOn) {
            return false;
          }
          return true;
        }) as MachineEvent[],
        id,
      })
    );
  };

export const getWallboardEventTimeline =
  ({ id }: WallboardEventTimelineParams, signal?: AbortSignal): AppThunk =>
  async (dispatch, getState) => {
    const {
      machines: { wallboardTimelineEndDate, wallboardSelectedLevel },
    } = getState();

    let start = localTime().utc().subtract(1, 'day').format();
    let end = localTime().utc().format();

    if (wallboardSelectedLevel === 'last24' && !isSameDay(wallboardTimelineEndDate)) {
      start = localTime(wallboardTimelineEndDate).utc().subtract(1, 'day').format();
      end = localTime(wallboardTimelineEndDate).utc().format();
    }

    if (wallboardSelectedLevel === 'today' && !isSameDay(wallboardTimelineEndDate)) {
      start = localTime(wallboardTimelineEndDate).startOf('day').utc().format();
      end = localTime(wallboardTimelineEndDate).endOf('day').utc().format();
    }

    const params = {
      machineId: id,
      from: start,
      until: end,
      pagination: {
        pageNumber: 1,
        pageSize: 1000,
        includeCount: true,
      },
    };

    const resp = await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.machineEventTimeline, {
          params,
          signal,
          ignoreDefaultErrorHandler: true,
          onError: (e: unknown) => {
            console.log(e, 'error get data');
          },
        })
      )
    );
    if (resp) {
      const data = (resp as Record<string, unknown>).machineEvents as Array<Record<string, unknown>>;

      const filtered = data.reduce((accumulator: Record<string, unknown>[], item, index) => {
        if (index === 0) {
          item.startedOn = start;
        }

        return [
          ...accumulator,
          {
            ...item,
            durationInSeconds: moment.utc(item.endedOn as string).diff(moment.utc(item.startedOn as string), 'seconds'),
          },
        ];
      }, []);

      dispatch(saveWallboardEventTimeline({ data: filtered as MachineEvent[], id }));
    }
  };

export const getMachineEventTimeline =
  (
    { id = null, start, end, pageNumber = 1, prevData = [], useAsyncFlowForRealtimeQuery }: MachineEventTimelineParams,
    signal?: AbortSignal
  ): AppThunk<Promise<unknown>> =>
  async (dispatch) => {
    const params = {
      machineId: id,
      from: start,
      until: end,
      useAsyncFlowForRealtimeQuery,
      pagination: {
        pageNumber,
        pageSize: 1000,
        includeCount: true,
      },
    };

    const resp = await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.machineEventTimelineDetail, {
          params,
          signal,
          ignoreDefaultErrorHandler: true,
          onError: (e: unknown) => {
            console.log(e, 'error get data');
          },
        })
      )
    );

    if ((resp as Record<string, Record<string, unknown>>)?.headers?.paginationString) {
      try {
        const pagination = JSON.parse((resp as Record<string, Record<string, string>>).headers.paginationString);
        if (pagination?.HasNext && (resp as Record<string, unknown>).machineEvents) {
          return dispatch(
            getMachineEventTimeline(
              {
                id,
                start,
                end,
                pageNumber: pageNumber + 1,
                prevData: [...prevData, ...((resp as Record<string, unknown>).machineEvents as unknown[])],
              },
              signal
            )
          );
        }
      } catch (e) {}
    }
    if (resp) {
      const data = [...prevData, ...((resp as Record<string, unknown>).machineEvents as unknown[])] as Array<Record<string, unknown>>;

      const groupedByNCFile: Record<string, unknown>[] = [];
      const groupedByBatch: Record<string, unknown>[] = [];

      const filtered = data.reduce((accumulator: Record<string, unknown>[], item, index) => {
        if (moment.utc(item.startedOn as string).isBefore(moment.utc(start))) {
          item.startedOn = start;
        }
        if (moment.utc(item.endedOn as string).isAfter(moment.utc(end))) {
          item.endedOn = end;
        }

        const durationInSeconds = moment.utc(item.endedOn as string).diff(moment.utc(item.startedOn as string), 'seconds');

        const lastGroupedItemByNCFile = groupedByNCFile[groupedByNCFile.length - 1];
        const lastGroupedItemByBatch = groupedByBatch[groupedByBatch.length - 1];

        const element = {
          ...item,
          durationInSeconds,
        };

        if (!lastGroupedItemByNCFile || lastGroupedItemByNCFile.ncFileMain !== element.ncFileMain) {
          groupedByNCFile.push(element);
        } else {
          groupedByNCFile[groupedByNCFile.length - 1] = {
            ...lastGroupedItemByNCFile,
            durationInSeconds: (element.durationInSeconds as number) + (lastGroupedItemByNCFile.durationInSeconds as number),
            endedOn: element.endedOn,
          };
        }

        if (
          !lastGroupedItemByBatch ||
          (element.productionBatchId != null && element.productionBatchId !== lastGroupedItemByBatch.productionBatchId)
        ) {
          groupedByBatch.push(element);
        } else {
          groupedByBatch[groupedByBatch.length - 1] = {
            ...lastGroupedItemByBatch,
            durationInSeconds: (element.durationInSeconds as number) + (lastGroupedItemByBatch.durationInSeconds as number),
            endedOn: element.endedOn,
          };
        }

        return [...accumulator, element];
      }, []);

      return { events: filtered, groupedByNCFile, groupedByBatch };
    }

    return { events: [], groupedByNCFile: [], groupedByBatch: [] };
  };

export const getMachineEventTimelineByBatchId = (params: Record<string, unknown>, signal?: AbortSignal) => async (dispatch: AppDispatch) => {
  // Tour demo data: return cached events instead of calling backend
  const demoEvents = getDemoTimelineEvents(params.productionBatchId as number);
  if (demoEvents) return { machineEvents: demoEvents };

  return await dispatch(
    backendCall(
      createApiCall((api) => api.manufacture.machineEventTimelineDetail, {
        params,
        signal,
        ignoreDefaultErrorHandler: true,
        onError: (e: unknown) => {
          console.log(e, 'error get data');
        },
      })
    )
  );
};

export const getMachineOperationsSearch =
  (
    { id, start, end, pageNumber = 1, prevData = [], orderBy, oDataFilter, select }: MachineOperationsSearchParams,
    signal?: AbortSignal
  ): AppThunk<Promise<unknown>> =>
  async (dispatch) => {
    const pageSize = 1000;
    const params = {
      search: '',
      top: pageSize,
      skip: pageSize * (pageNumber - 1),
      orderBy: orderBy ?? ['ScheduledStartDate asc'],
      oDataFilter:
        oDataFilter ?? `((ScheduledEndDate ne null and ScheduledEndDate gt ${start}) or (ScheduledEndDate eq null)) and ScheduledStartDate lt ${end}`,
      ...(select ? { select } : {}),
    };

    const resp = await dispatch(
      backendCall(
        createApiCall((api) => api.search.operationsSearch, {
          params,
          signal,
          ignoreDefaultErrorHandler: true,
          onError: (e: unknown) => {
            console.log(e, 'error get data');
          },
        })
      )
    );
    if ((resp as Record<string, Record<string, unknown>>)?.headers?.paginationString) {
      try {
        const pagination = JSON.parse((resp as Record<string, Record<string, string>>).headers.paginationString);
        if (pagination?.HasNext && (resp as Record<string, unknown>).searchResult) {
          return dispatch(
            getMachineOperationsSearch(
              {
                id,
                start,
                end,
                pageNumber: pageNumber + 1,
                prevData: [...prevData, ...((resp as Record<string, unknown>).searchResult as unknown[])],
              },
              signal
            )
          );
        }
      } catch (e) {}
    }
    if (resp) {
      const data = [...prevData, ...((resp as Record<string, unknown>).searchResult as unknown[])] as Array<Record<string, unknown>>;

      const filtered = data.reduce((accumulator: Record<string, unknown>[], item) => {
        if (moment.utc(item.scheduledStartDate as string).isBefore(moment.utc(start))) {
          item.scheduledStartDate = start;
        }
        if (moment.utc(item.scheduledEndDate as string).isAfter(moment.utc(end))) {
          item.scheduledEndDate = end;
        }

        return [
          ...accumulator,
          {
            ...item,
            durationInSeconds: moment.utc(item.scheduledEndDate as string).diff(moment.utc(item.scheduledStartDate as string), 'seconds'),
          },
        ];
      }, []);

      return filtered;
    }

    return [];
  };

export const getMachineProductionBatches =
  ({ id, start, end, select }: MachineProductionBatchesParams, signal?: AbortSignal) =>
  async (dispatch: AppDispatch) => {
    const params = {
      search: '',
      orderBy: ['EndedOn asc'],
      oDataFilter: `MachineId eq ${id} and EndedOn gt ${start} and StartedOn lt ${end}`,
      ...(select ? { select } : {}),
    };

    const resp = await dispatch(
      backendCall(
        createApiCall((api) => api.search.productionBatches, {
          params,
          signal,
          ignoreDefaultErrorHandler: true,
          onError: (e: unknown) => {
            console.log(e, 'error get data');
          },
        })
      )
    );

    if ((resp as Record<string, unknown>)?.searchResult) {
      const countsData = (
        ((resp as Record<string, unknown>).searchResult as Array<Record<string, unknown>>).reduce((accumulator: Record<string, unknown>[], i) => {
          const storage: Record<string, unknown>[] = [];
          let quantityDelta = 0;
          (i.quantityEvents as Array<Record<string, unknown>>)?.map((item) => {
            quantityDelta += item.quantityDelta as number;
            if (moment.utc(item.startedOn as string).isBefore(moment.utc(start))) {
              item.startedOn = start;
            }
            if (moment.utc(item.endedOn as string).isAfter(moment.utc(end)) || moment.utc(item.endedOn as string).isBefore(moment.utc(start))) {
              return;
            }

            storage.push({
              ...item,
              quantityDelta,
              ncFileId: i.ncFileId,
            });
          });

          return [...accumulator, ...storage];
        }, []) as Array<Record<string, unknown>>
      ).sort((a, b) => new Date(a.endedOn as string).getTime() - new Date(b.endedOn as string).getTime());

      return {
        batches: (resp as Record<string, unknown>).searchResult,
        counts: countsData,
      };
    }
    return {
      batches: [],
      counts: [],
    };
  };

export const getMachineAlarmEvent =
  ({ id, start, end, productionBatchId }: MachineAlarmEventParams, signal?: AbortSignal) =>
  async (dispatch: AppDispatch) => {
    const params: Record<string, unknown> = {
      ...(id ? { id } : {}),
      ...(start ? { start } : {}),
      ...(end ? { end } : {}),
      ...(productionBatchId ? { productionBatchId } : {}),
    };

    const resp = await dispatch(
      backendCall(
        createApiCall((api) => api.machines.machineAlarmEvent, {
          params,
          signal,
          ignoreDefaultErrorHandler: true,
          onError: (e: unknown) => {
            console.log(e, 'error get data');
          },
        })
      )
    );

    if (resp) {
      const alarmEvents = (resp as Array<Record<string, unknown>>).reduce((accumulator: Record<string, unknown>[], item) => {
        if (moment.utc(item.startedOn as string).isBefore(moment.utc(start))) {
          item.startedOn = start;
        }
        if (moment.utc(item.endedOn as string).isAfter(moment.utc(end))) {
          item.endedOn = end;
        }

        return [
          ...accumulator,
          {
            ...item,
            durationInSeconds: moment.utc(item.endedOn as string).diff(moment.utc(item.startedOn as string), 'seconds'),
          },
        ];
      }, []);

      if (!alarmEvents.length) return [];

      const alarmGroups: Array<Record<string, unknown>[]> = Array.from({ length: 5 }, () => []);

      function isOverlapping(nextAlarm: Record<string, unknown>, currentAlarm: Record<string, unknown>) {
        return moment(nextAlarm.startedOn as string).isBefore(moment(currentAlarm.endedOn as string));
      }

      for (const currentAlarm of alarmEvents) {
        for (const index in alarmGroups) {
          const group = alarmGroups[index];
          const lastInGroup = group.at(-1);
          if (lastInGroup) {
            if (!isOverlapping(currentAlarm, lastInGroup)) {
              group.push(currentAlarm);
              break;
            } else {
              if (Number(index) === alarmGroups.length - 1) {
                group.push(currentAlarm);
                break;
              }
            }
          } else {
            group.push(currentAlarm);
            break;
          }
        }
      }
      return alarmGroups.filter((group) => group.length);
    }
    return [];
  };

const mergeMachineStatusImage = (
  machineStatus: NonNullable<Machine['machineStatus']>,
  machineImage: NonNullable<Machine['machineImage']>
): NonNullable<Machine['machineStatus']> => {
  const isLocal = (machineImage as { local?: boolean }).local === true;
  const localUrl = (machineImage as { url?: string }).url;
  return {
    ...machineStatus,
    imageData: {
      extensionDerivative: machineImage.extensionDerivative,
      extensionOriginal: machineImage.extensionOriginal,
      isCropped: machineImage.isCropped,
      pageNumber: machineImage.pageNumber,
      scopeId: machineImage.scopeId,
      ...(isLocal && localUrl ? { local: true, url: localUrl } : {}),
    } as NonNullable<Machine['machineStatus']>['imageData'],
    imageDetails: machineImage.imagePageDetails,
    imageHash: machineImage.contentHash ?? (isLocal && machineImage.documentId != null ? String(machineImage.documentId) : undefined),
  };
};

export default createReducer<MachinesState>(initState, {
  [setWallboardSelectedLevel.type]: (state: MachinesState, action: { payload: string }) => ({
    ...state,
    wallboardSelectedLevel: action.payload,
  }),
  [setWallboardTimelineEndDate.type]: (state: MachinesState, action: { payload: Moment }) => ({
    ...state,
    wallboardTimelineEndDate: action.payload,
  }),
  [setWallboardTimelineStorageNotLoaded.type]: (state: MachinesState, action: { payload: WallboardTimelineNotLoadedPayload }) => ({
    ...state,
    wallboardTimelineStorageLoaded: {
      ...state.wallboardTimelineStorageLoaded,
      [action.payload.id]: false,
    },
    wallboardTimelineStorage: {
      ...state.wallboardTimelineStorage,
      [action.payload.id]: [],
    },
  }),
  [saveWallboardEventTimeline.type]: (state: MachinesState, action: { payload: WallboardTimelineEventData }) => ({
    ...state,
    wallboardTimelineStorage: {
      ...state.wallboardTimelineStorage,
      [action.payload.id]: action.payload.data,
    },
    wallboardTimelineStorageLoaded: {
      ...state.wallboardTimelineStorageLoaded,
      [action.payload.id]: true,
    },
  }),
  [refreshInfo.type]: (state: MachinesState, action: { payload: Record<string, unknown> }) => ({
    ...state,
    refreshInfo: action.payload,
  }),
  [setRequestTimeEnd.type]: (state: MachinesState, action: { payload: number }) => ({
    ...state,
    requestTimeEndMS: action.payload,
  }),
  [saveMachineAddresses.type]: (state: MachinesState, action: { payload: MachineAddress[] }) => ({
    ...state,
    machineAddresses: action.payload,
    loadedMachineAddresses: true,
    loadedMachineAddressesError: false,
  }),
  [setNCFileStatistics.type]: (state: MachinesState, action: { payload: NcFileStatisticsData }) => ({
    ...state,
    ncFileStatistics: {
      ...state.ncFileStatistics,
      ...action.payload,
    },
  }),
  [saveMachineAddress.type]: (state: MachinesState, action: { payload: Machine }) => ({
    ...state,
    machineAddresses: [...((state?.machineAddresses as unknown as Record<string, unknown>)?.machineAddress as unknown as Machine[]), action.payload],
    loadedMachineAddresses: true,
    loadedMachineAddressesError: false,
  }),
  [requestMachineAddresses.type]: (state: MachinesState) => ({
    ...state,
    machineAddresses: [],
    loadedMachineAddresses: false,
    loadedMachineAddressesError: false,
  }),
  [requestMachineAddressesError.type]: (state: MachinesState) => ({
    ...state,
    loadedMachineAddressesError: true,
  }),
  [setMachineBlobAccessInfo.type]: (state: MachinesState) => ({
    ...state,
  }),
  [setSelectedMachine.type]: (state: MachinesState, action: { payload: Partial<Machine> }) => {
    if (action?.payload?.id) {
      addQueryParams({ id: action.payload.id });
    }

    // Clear needUpdateAfterLoad flag when explicitly selecting a new machine
    const selectedMachine = action.payload
      ? {
          ...action.payload,
          needUpdateAfterLoad: false,
        }
      : {};

    return {
      ...state,
      selectedItem: selectedMachine,
    };
  },
  [setSelectedNCFile.type]: (state: MachinesState, action: { payload: Record<string, unknown> }) => ({
    ...state,
    selectedNCFileItem: action.payload,
  }),
  [setSelectedTime.type]: (state: MachinesState, action: { payload: string }) => ({
    ...state,
    selectedTime: action.payload,
  }),
  [setMachines.type]: (state: MachinesState, action: { payload: Machine[] }) => {
    // Only preserve demo machines while a tour is actively running.
    // Outside of tours, setMachines replaces the array as-is (original behavior).
    if (!isTourActive()) {
      return { ...state, machinesLoaded: true, isError: false, machines: action.payload };
    }
    const demoMachines = state.machines.filter((m) => m.machineId! >= 999990000);
    if (demoMachines.length === 0) {
      return { ...state, machinesLoaded: true, isError: false, machines: action.payload };
    }
    const realPayload = action.payload.filter((m) => m.machineId! < 999990000);
    return { ...state, machinesLoaded: true, isError: false, machines: [...realPayload, ...demoMachines] };
  },
  [setInspectedMachine.type]: (state: MachinesState, action: { payload: Machine | null }) => ({
    ...state,
    inspectedMachine: action.payload,
  }),
  [clearInspectedMachine.type]: (state: MachinesState) => ({
    ...state,
    inspectedMachine: null,
  }),
  [clearMachineImageByDocumentId.type]: (state: MachinesState, action: { payload: { documentId: number; machineId?: number } }) => {
    const { documentId, machineId } = action.payload;

    const stripImage = (m: Machine): Machine => {
      let next = m;
      if (m.machineImage?.documentId === documentId) {
        const { machineImage, ...rest } = next;
        next = rest as Machine;
      }
      if (next.machineStatus?.imageDocumentId === documentId) {
        const { imageData, imageDetails, imageDocumentId, imageHash, ...statusRest } = next.machineStatus;
        next = { ...next, machineStatus: statusRest };
      }
      return next;
    };

    const matches = (m: Machine | null | undefined) =>
      !!m &&
      (machineId == null || m.machineId === machineId) &&
      (m.machineImage?.documentId === documentId || m.machineStatus?.imageDocumentId === documentId);

    const machinesAffected = state.machines.some(matches);
    const inspectedAffected = matches(state.inspectedMachine);

    if (!machinesAffected && !inspectedAffected) return state;

    return {
      ...state,
      machines: machinesAffected ? state.machines.map((m) => (matches(m) ? stripImage(m) : m)) : state.machines,
      inspectedMachine: inspectedAffected && state.inspectedMachine ? stripImage(state.inspectedMachine) : state.inspectedMachine,
    };
  },
  [setMachineImage.type]: (state: MachinesState, action: { payload: { machineId: number; machineImage: NonNullable<Machine['machineImage']> } }) => {
    const { machineId, machineImage } = action.payload;

    const applyImage = (m: Machine): Machine => {
      if (m.machineId !== machineId) return m;
      const next: Machine = { ...m, machineImage };
      if (m.machineStatus?.imageDocumentId === machineImage.documentId) {
        next.machineStatus = mergeMachineStatusImage(m.machineStatus, machineImage);
      }
      return next;
    };

    return {
      ...state,
      machines: state.machines.map(applyImage),
      inspectedMachine: state.inspectedMachine ? applyImage(state.inspectedMachine) : state.inspectedMachine,
    };
  },
  [applyMachineImageByIndirectLinks.type]: (
    state: MachinesState,
    action: { payload: { links: NonNullable<Document['links']>; machineImage: NonNullable<Machine['machineImage']> } }
  ) => {
    const { links, machineImage } = action.payload;

    const applyImage = (m: Machine): Machine => {
      if (!m.machineStatus || !matchesAnyIndirectLink(m.machineStatus, links)) return m;
      return { ...m, machineStatus: mergeMachineStatusImage(m.machineStatus, machineImage) };
    };

    return {
      ...state,
      machines: state.machines.map(applyImage),
      inspectedMachine: state.inspectedMachine ? applyImage(state.inspectedMachine) : state.inspectedMachine,
    };
  },
  [replaceMachine.type]: (state: MachinesState, action: { payload: Machine }) => ({
    ...state,
    machines: state.machines.map((m) => (m.machineId === action.payload.machineId ? action.payload : m)),
    inspectedMachine: state.inspectedMachine?.machineId === action.payload.machineId ? action.payload : state.inspectedMachine,
  }),
  [setMachineStatistics.type]: (state: MachinesState, action: { payload: MachineStatisticsData }) => ({
    ...state,
    machineStatistics: action.payload,
  }),
  [setMachineStatisticsForNCFile.type]: (state: MachinesState, action: { payload: MachineStatisticsData }) => ({
    ...state,
    machineStatisticsForNCFile: action.payload,
  }),
  [setMinimumTileSize.type]: (state: MachinesState, action: { payload: number }) => ({
    ...state,
    minimumTileSize: action.payload,
  }),
  [setMachinesGroup.type]: (state: MachinesState, action: { payload: MachineGroup[] }) => ({
    ...state,
    machinesGroup: action.payload,
    machinesGroupLoaded: true,
  }),
  [setMachinesReferencesLoaded.type]: (state: MachinesState, action: { payload: boolean }) => ({
    ...state,
    machinesReferencesLoaded: action.payload,
  }),
  [setMachinesReferences.type]: (state: MachinesState, action: { payload: MachineReference[] }) => ({
    ...state,
    machinesReferences: action.payload,
    machinesReferencesLoaded: true,
  }),
  [setMachinesOperations.type]: (state: MachinesState, action: { payload: Operation[] }) => ({
    ...state,
    machinesOperations: action.payload,
    machinesOperationsLoaded: true,
  }),
  [upsertMachinesOperation.type]: (state: MachinesState, action: { payload: Operation }) => {
    const idx = state.machinesOperations.findIndex((o) => o.operationId === action.payload.operationId);
    if (idx === -1) {
      return { ...state, machinesOperations: [...state.machinesOperations, action.payload] };
    }
    const next = state.machinesOperations.slice();
    next[idx] = action.payload;
    return { ...state, machinesOperations: next };
  },
  [setMachinesOperationsLoaded.type]: (state: MachinesState, action: { payload: boolean }) => ({
    ...state,
    machinesOperationsLoaded: action.payload,
  }),
  [onUpdatedMachineEventsTimeline.type]: (state: MachinesState) => ({
    ...state,
    updatedMachineEventsTimelineHash: `${Math.round(Math.random() * 100)}-${new Date().getTime()}`,
  }),
  [setMachinesError.type]: (state: MachinesState) => ({
    ...state,
    isError: true,
  }),
  [clearMachines.type]: () => {
    return initState;
  },
  [clear.type]: () => {
    return initState;
  },
  [injectDemoMachine.type]: (state: MachinesState, action: { payload: Machine }) => {
    const exists = state.machines.some((m) => m.machineId === action.payload.machineId);
    return {
      ...state,
      machinesLoaded: true,
      machines: exists ? state.machines : [...state.machines, action.payload],
    };
  },
  [prependDemoMachine.type]: (state: MachinesState, action: { payload: Machine }) => {
    const exists = state.machines.some((m) => m.machineId === action.payload.machineId);
    return {
      ...state,
      machinesLoaded: true,
      machines: exists ? state.machines : [action.payload, ...state.machines],
    };
  },
  [removeDemoMachine.type]: (state: MachinesState, action: { payload: number }) => ({
    ...state,
    machines: state.machines.filter((m) => m.machineId !== action.payload),
  }),
});
